<template>
  <div class="companies-page">
    <div class="companies-container">
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('companies.title') }}</h1>
          <p class="page-subtitle">{{ $t('companies.subtitle') }}</p>
          <button @click="showSettings = true" class="btn-settings">
            <LineIcon name="settings" :size="15" :stroke-width="1.8" />
            {{ $t('companies.settings') }}
          </button>
        </div>
        <div class="header-actions">
          <div class="search-box">
            <LineIcon name="search" :size="15" :stroke-width="1.8" class="search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('companies.searchPlaceholder')"
              class="search-input"
            />
          </div>
          <button @click="showCreateModal = true" class="btn-create">
            <LineIcon name="plus" :size="15" :stroke-width="1.8" />
            {{ $t('companies.addCompany') }}
          </button>
        </div>
      </div>

      <!-- Settings Section (inline, dashboard-style) -->
      <div v-if="showSettings" class="settings-section">
        <div class="settings-section-header">
          <div class="settings-section-title">
            <LineIcon name="settings" :size="18" :stroke-width="1.8" />
            <h2>{{ $t('companies.companySettings') }}</h2>
          </div>
          <button @click="showSettings = false" class="btn-close-settings">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
            {{ $t('common.close') }}
          </button>
        </div>
        <div class="settings-section-body">
          <!-- Subtotal Validation -->
          <div class="settings-card" :class="{ active: bulkValidationSetting }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-amber">
                <LineIcon name="calculator" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.subtotalValidation') }}</h3>
                <p>{{ $t('companies.subtotalValidationDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkValidationSetting" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- VAT Conflict Validation -->
          <div class="settings-card" :class="{ active: bulkVatConflictValidation }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-amber">
                <LineIcon name="alert-triangle" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.vatConflictValidation') }}</h3>
                <p>{{ $t('companies.vatConflictValidationDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkVatConflictValidation" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Company Scan Validation -->
          <div class="settings-card" :class="{ active: bulkCompanyScanValidation }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-blue">
                <LineIcon name="building" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.companyScanValidation') }}</h3>
                <p>{{ $t('companies.companyScanValidationDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkCompanyScanValidation" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Warehouse Usage -->
          <div class="settings-card" :class="{ active: bulkUseWarehouse }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-orange">
                <LineIcon name="warehouse" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.useWarehouse') }}</h3>
                <p>{{ $t('companies.useWarehouseDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkUseWarehouse" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Vendor Code Usage -->
          <div class="settings-card" :class="{ active: bulkUseVendorCode }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-blue">
                <LineIcon name="barcode" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.useVendorCode') }}</h3>
                <p>{{ $t('companies.useVendorCodeDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkUseVendorCode" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Gas Station Multi-Invoice Splitting -->
          <div class="settings-card" :class="{ active: bulkGasStationMultiInvoice }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-orange">
                <LineIcon name="fuel-pump" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.gasStationMultiInvoice', 'Gas station multi-invoice') }}</h3>
                <p>{{ $t('companies.gasStationMultiInvoiceDescription', 'When an uploaded image contains several invoices/receipts, automatically detect, crop and extract each one as a separate invoice.') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkGasStationMultiInvoice" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Pack-size quantity conversion (Santa export) -->
          <div class="settings-card" :class="{ active: bulkUseQuantityMultiplier }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-blue">
                <LineIcon name="arrow-left-right" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.useQuantityMultiplier') }}</h3>
                <p>{{ $t('companies.useQuantityMultiplierDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkUseQuantityMultiplier" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Santa: confirm the lines booked as a service -->
          <div class="settings-card" :class="{ active: bulkConfirmServiceLines }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-blue">
                <LineIcon name="wrench" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.confirmServiceLines') }}</h3>
                <p>{{ $t('companies.confirmServiceLinesDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkConfirmServiceLines" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Clients Tab Usage -->
          <div class="settings-card" :class="{ active: bulkUseClients }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-orange">
                <LineIcon name="users" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.useClients') }}</h3>
                <p>{{ $t('companies.useClientsDescription') }}</p>
              </div>
            </div>
            <button
              v-if="bulkUseClients"
              type="button"
              class="settings-gear-btn"
              @click.stop="openClientsCompaniesModal"
              :title="$t('companies.configureClients', 'Настройки')"
            >
              <LineIcon name="settings" :size="14" :stroke-width="1.8" />
            </button>
            <label class="toggle">
              <input type="checkbox" v-model="bulkUseClients" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <!-- Accounting Date Usage -->
          <div class="settings-card" :class="{ active: bulkUseAccountingDate }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-green">
                <LineIcon name="calendar" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.useAccountingDate') }}</h3>
                <p>{{ $t('companies.useAccountingDateDescription') }}</p>
              </div>
            </div>
            <button 
              v-if="bulkUseAccountingDate"
              type="button"
              class="settings-gear-btn"
              @click.stop="openAccountingDateSettingsModal"
              :title="$t('companies.configureAccountingDate', 'Настройки')"
            >
              <LineIcon name="settings" :size="14" :stroke-width="1.8" />
            </button>
            <label class="toggle">
              <input type="checkbox" v-model="bulkUseAccountingDate" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>

          <div class="settings-card" :class="{ active: bulkCreditNoteNegative }">
            <div class="settings-card-left">
              <div class="settings-icon settings-icon-orange">
                <LineIcon name="minus" :size="15" :stroke-width="1.8" />
              </div>
              <div class="settings-info">
                <h3>{{ $t('companies.creditNoteNegative') }}</h3>
                <p>{{ $t('companies.creditNoteNegativeDescription') }}</p>
              </div>
            </div>
            <label class="toggle">
              <input type="checkbox" v-model="bulkCreditNoteNegative" @change="saveBulkSettings" />
              <span class="toggle-track"></span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="filteredCompanies.length === 0 && searchQuery" class="empty-state">
        <LineIcon name="search" :size="15" :stroke-width="1.8" style="font-size: 80px; color: #cbd5e1;" />
        <h3>{{ $t('companies.noCompanies') }}</h3>
        <p>{{ $t('common.search') }}: "{{ searchQuery }}"</p>
      </div>

      <div v-else-if="companies.length === 0" class="empty-state">
        <LineIcon name="building" :size="15" :stroke-width="1.8" style="font-size: 80px; color: #cbd5e1;" />
        <h3>{{ $t('companies.noCompanies') }}</h3>
        <p>{{ $t('companies.createFirst') }}</p>
        <button @click="showCreateModal = true" class="btn-primary">
          <LineIcon name="plus" :size="15" :stroke-width="1.8" />
          {{ $t('companies.addCompany') }}
        </button>
      </div>

      <div v-else class="companies-grid">
        <div
          v-for="company in filteredCompanies"
          :key="company.id"
          class="company-card"
          :class="{ 'inactive': !company.is_active }"
          @click="selectCompany(company)"
        >
          <div class="company-header">
            <div class="company-name-wrapper">
              <h3 class="company-name">{{ company.name }}</h3>
              <span v-if="!company.is_active" class="inactive-label">{{ $t('common.inactive') }}</span>
            </div>
            <div class="company-actions" @click.stop>
              <button @click="editCompany(company)" class="btn-icon has-tip has-tip--up" :data-label="$t('common.edit')" :aria-label="$t('common.edit')">
                <LineIcon name="pencil" :size="14" :stroke-width="1.8" />
              </button>
              <button @click="deleteCompany(company)" class="btn-icon btn-icon-danger has-tip has-tip--up" :data-label="$t('common.delete')" :aria-label="$t('common.delete')">
                <LineIcon name="trash" :size="14" :stroke-width="1.8" />
              </button>
            </div>
          </div>

          <div class="company-details">
            <div v-if="company.vat_number" class="detail-row">
              <span class="detail-label">VAT:</span>
              <span class="detail-value">{{ company.vat_number }}</span>
            </div>
            <div v-if="company.address" class="detail-row">
              <span class="detail-label">Address:</span>
              <span class="detail-value">{{ company.address }}</span>
            </div>
            <div v-if="company.email" class="detail-row">
              <span class="detail-label">Email:</span>
              <span class="detail-value">{{ company.email }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Company Modal -->
    <CompanyModalEnhanced
      v-if="showCreateModal || editingCompany"
      :company="editingCompany"
      @close="closeModal"
      @saved="handleSaved"
    />

    <!-- Clients Per-Company Settings Modal -->
    <Teleport to="body">
      <div v-if="showClientsCompaniesModal" class="modal-backdrop" @click.self="closeClientsCompaniesModal">
        <div class="modal-container" @click.stop>
          <div class="modal-content">
            <div class="modal-header">
              <div class="modal-title-section">
                <h2 class="modal-title">{{ $t('companies.clientsScopeTitle', 'За кои фирми да се използват клиенти') }}</h2>
              </div>
              <button @click="closeClientsCompaniesModal" class="btn btn-icon">
                <LineIcon name="x" :size="15" :stroke-width="1.8" />
              </button>
            </div>
            <div class="modal-body">
              <div class="clients-scope-options">
                <label class="clients-scope-option">
                  <input
                    type="radio"
                    :value="'all'"
                    v-model="clientsScopeMode"
                  />
                  <div>
                    <div class="clients-scope-label">{{ $t('companies.clientsScopeAll', 'Всички фирми') }}</div>
                    <div class="clients-scope-hint">{{ $t('companies.clientsScopeAllHint', 'Разделът „Клиенти“ ще се показва за всички ваши фирми.') }}</div>
                  </div>
                </label>
                <label class="clients-scope-option">
                  <input
                    type="radio"
                    :value="'specific'"
                    v-model="clientsScopeMode"
                  />
                  <div>
                    <div class="clients-scope-label">{{ $t('companies.clientsScopeSpecific', 'Само избрани фирми') }}</div>
                    <div class="clients-scope-hint">{{ $t('companies.clientsScopeSpecificHint', 'Разделът „Клиенти“ ще се показва само за фирмите, които изберете по-долу.') }}</div>
                  </div>
                </label>
              </div>

              <div v-if="clientsScopeMode === 'specific'" class="clients-companies-list">
                <div v-if="companies.length === 0" class="clients-companies-empty">
                  {{ $t('companies.noCompanies', 'Няма намерени фирми') }}
                </div>
                <label
                  v-for="company in companies"
                  :key="company.id"
                  class="clients-company-row"
                >
                  <input
                    type="checkbox"
                    :value="company.id"
                    v-model="clientsCompanyIdsDraft"
                  />
                  <span class="clients-company-name">{{ company.name }}</span>
                </label>
              </div>
            </div>
            <div class="modal-footer">
              <button @click="saveClientsCompanies" class="btn btn-primary">
                {{ $t('common.save', 'Запази') }}
              </button>
              <button @click="closeClientsCompaniesModal" class="btn btn-secondary">
                {{ $t('common.cancel', 'Отказ') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Accounting Date Settings Modal -->
    <Teleport to="body">
      <div v-if="showAccountingDateSettingsModal" class="modal-backdrop" @click.self="closeAccountingDateSettingsModal">
        <div class="modal-container" @click.stop>
          <div class="modal-content">
            <div class="modal-header">
              <div class="modal-title-section">
                <h2 class="modal-title">{{ $t('companies.accountingDateSettings', 'Настройки на дата на осчетоводяване') }}</h2>
              </div>
              <button @click="closeAccountingDateSettingsModal" class="btn btn-icon">
                <LineIcon name="x" :size="15" :stroke-width="1.8" />
              </button>
            </div>
            <div class="modal-body">
              <p class="accounting-date-hint">
                {{ $t('companies.accountingDateMonthsBackHint', 'Колко месеца назад да се приложи автоматично дата осчетоводяване? Фактури по-стари от този период ще запазят оригиналната си дата.') }}
              </p>
              <div class="form-group">
                <label for="accountingDateMonthsBack">{{ $t('companies.monthsBack', 'Месеци назад') }}</label>
                <SelectField v-model="accountingDateMonthsBack" :options="monthsBackOptions" class="form-control" />
              </div>
            </div>
            <div class="modal-footer">
              <button @click="saveAccountingDateSettings" class="btn btn-primary">
                {{ $t('common.save', 'Запази') }}
              </button>
              <button @click="closeAccountingDateSettingsModal" class="btn btn-secondary">
                {{ $t('common.cancel', 'Отказ') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import SelectField from '@/components/SelectField.vue';
import { ref, computed, onMounted, watch } from 'vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { useRouter } from 'vue-router';
import api from '@/utils/api';
import { useCompanies } from '@/composables/useCompanyVendor';
import { useAuthStore } from '@/stores/auth';
import CompanyModalEnhanced from '@/components/companies/CompanyModalEnhanced.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();

const { companies: companiesFromStore, loadCompanies: loadCompaniesStore } = useCompanies();
const companies = computed(() => companiesFromStore.value);
const loading = ref(true);
const showCreateModal = ref(false);
const editingCompany = ref(null);
const searchQuery = ref('');
const showSettings = ref(false);
const bulkValidationSetting = ref(false);
const bulkVatConflictValidation = ref(true);
const bulkCompanyScanValidation = ref(false);
const bulkUseWarehouse = ref(false);
const bulkUseVendorCode = ref(false);
const bulkUseClients = ref(false);
const bulkUseAccountingDate = ref(false);
const bulkGasStationMultiInvoice = ref(false);
const bulkCreditNoteNegative = ref(false);
const bulkUseQuantityMultiplier = ref(false);
const bulkConfirmServiceLines = ref(false);

// Accounting date settings modal
const showAccountingDateSettingsModal = ref(false);
const accountingDateMonthsBack = ref(12);

// Clients per-company settings modal
const showClientsCompaniesModal = ref(false);
const clientsScopeMode = ref('all'); // 'all' | 'specific'
const clientsCompanyIdsDraft = ref([]);

// Watch for settings modal opening to load current values from auth store
watch(showSettings, (newValue) => {
  if (newValue) {
    bulkValidationSetting.value = authStore.enforceSubtotalValidation;
    bulkVatConflictValidation.value = authStore.validateVatConflicts;
    bulkCompanyScanValidation.value = authStore.validateCompanyOnScan;
    bulkUseWarehouse.value = authStore.useWarehouse || false;
    bulkUseVendorCode.value = authStore.useVendorCode || false;
    bulkUseClients.value = authStore.useClients || false;
    bulkUseAccountingDate.value = authStore.useAccountingDate || false;
    bulkGasStationMultiInvoice.value = authStore.gasStationMultiInvoice || false;
    bulkCreditNoteNegative.value = authStore.creditNoteNegative || false;
    bulkUseQuantityMultiplier.value = authStore.useQuantityMultiplier || false;
    bulkConfirmServiceLines.value = authStore.confirmServiceLines || false;
    accountingDateMonthsBack.value = authStore.accountingDateMonthsBack ?? 12;
    console.log('Settings modal opened, loaded values from user settings:', {
      enforce_subtotal_validation: bulkValidationSetting.value,
      validate_vat_conflicts: bulkVatConflictValidation.value,
      validate_company_on_scan: bulkCompanyScanValidation.value,
      use_warehouse: bulkUseWarehouse.value,
      use_vendor_code: bulkUseVendorCode.value,
      gas_station_multi_invoice: bulkGasStationMultiInvoice.value,
      use_clients: bulkUseClients.value,
      use_accounting_date: bulkUseAccountingDate.value,
      credit_note_negative_amounts: bulkCreditNoteNegative.value,
      confirm_service_lines: bulkConfirmServiceLines.value,
      accounting_date_months_back: accountingDateMonthsBack.value
    });
  }
});

const filteredCompanies = computed(() => {
  let filtered = companies.value;

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(company => {
      const nameMatch = company.name?.toLowerCase().includes(query);
      const vatMatch = company.vat_number?.toLowerCase().includes(query);
      const emailMatch = company.email?.toLowerCase().includes(query);
      const addressMatch = company.address?.toLowerCase().includes(query);

      // Check alias names if they exist
      const aliasMatch = company.alias_names?.some(alias =>
        alias.toLowerCase().includes(query)
      );

      return nameMatch || vatMatch || emailMatch || addressMatch || aliasMatch;
    });
  }

  // Sort by active status (active first, then inactive)
  return filtered.sort((a, b) => {
    if (a.is_active === b.is_active) return 0;
    return a.is_active ? -1 : 1;
  });
});

const loadCompanies = async () => {
  try {
    loading.value = true;
    await loadCompaniesStore(true); // Force reload from API and update store
  } catch (error) {
    console.error('Failed to load companies:', error);
  } finally {
    loading.value = false;
  }
};

const editCompany = (company) => {
  editingCompany.value = company;
};

const selectCompany = (company) => {
  // Store in localStorage before redirecting
  localStorage.setItem('selectedCompanyId', company.id.toString());
  router.push(`/?companyId=${company.id}`);
};

const viewInvoices = (company) => {
  router.push(`/?companyId=${company.id}`);
};

const viewVendors = (company) => {
  router.push(`/companies/${company.id}/vendors`);
};

const viewAccountCodes = (company) => {
  router.push(`/companies/${company.id}/account-codes`);
};

const deleteCompany = async (company) => {
  if (!confirm(`Are you sure you want to delete "${company.name}"? This action cannot be undone.`)) {
    return;
  }

  try {
    await api.delete(`/api/companies/${company.id}`);
    
    // Clear from localStorage if it was the selected company
    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (selectedCompanyId && parseInt(selectedCompanyId) === company.id) {
      localStorage.removeItem('selectedCompanyId');
    }
    
    // Reload to refresh from server and update store
    await loadCompanies();
  } catch (error) {
    console.error('Failed to delete company:', error);
    alert('Failed to delete company. Please try again.');
  }
};

const closeModal = () => {
  showCreateModal.value = false;
  editingCompany.value = null;
};

const handleSaved = () => {
  closeModal();
  loadCompanies();
};

const saveBulkSettings = async () => {
  try {
    console.log('Saving user settings:', {
      enforce_subtotal_validation: bulkValidationSetting.value,
      validate_vat_conflicts: bulkVatConflictValidation.value,
      validate_company_on_scan: bulkCompanyScanValidation.value,
      use_warehouse: bulkUseWarehouse.value,
      use_vendor_code: bulkUseVendorCode.value,
      gas_station_multi_invoice: bulkGasStationMultiInvoice.value,
      use_clients: bulkUseClients.value,
      use_accounting_date: bulkUseAccountingDate.value,
      credit_note_negative_amounts: bulkCreditNoteNegative.value,
      confirm_service_lines: bulkConfirmServiceLines.value,
      use_quantity_multiplier: bulkUseQuantityMultiplier.value
    });

    const result = await authStore.updateSettings({
      enforce_subtotal_validation: bulkValidationSetting.value,
      validate_vat_conflicts: bulkVatConflictValidation.value,
      validate_company_on_scan: bulkCompanyScanValidation.value,
      use_warehouse: bulkUseWarehouse.value,
      use_vendor_code: bulkUseVendorCode.value,
      gas_station_multi_invoice: bulkGasStationMultiInvoice.value,
      use_clients: bulkUseClients.value,
      use_accounting_date: bulkUseAccountingDate.value,
      credit_note_negative_amounts: bulkCreditNoteNegative.value,
      confirm_service_lines: bulkConfirmServiceLines.value,
      use_quantity_multiplier: bulkUseQuantityMultiplier.value
    });

    if (!result.success) {
      throw new Error(result.error);
    }
    
    console.log('User settings saved successfully');
  } catch (error) {
    console.error('Failed to save settings:', error);
    alert('Failed to save settings. Please try again.');
  }
};

const openClientsCompaniesModal = () => {
  const stored = authStore.clientsCompanyIds || [];
  if (stored.length > 0) {
    clientsScopeMode.value = 'specific';
    clientsCompanyIdsDraft.value = [...stored];
  } else {
    clientsScopeMode.value = 'all';
    clientsCompanyIdsDraft.value = [];
  }
  showClientsCompaniesModal.value = true;
};

const closeClientsCompaniesModal = () => {
  showClientsCompaniesModal.value = false;
};

const saveClientsCompanies = async () => {
  try {
    const ids = clientsScopeMode.value === 'specific'
      ? [...clientsCompanyIdsDraft.value]
      : [];

    const result = await authStore.updateSettings({
      enforce_subtotal_validation: bulkValidationSetting.value,
      validate_vat_conflicts: bulkVatConflictValidation.value,
      validate_company_on_scan: bulkCompanyScanValidation.value,
      use_warehouse: bulkUseWarehouse.value,
      use_vendor_code: bulkUseVendorCode.value,
      gas_station_multi_invoice: bulkGasStationMultiInvoice.value,
      use_clients: bulkUseClients.value,
      clients_company_ids: ids,
      use_accounting_date: bulkUseAccountingDate.value,
      credit_note_negative_amounts: bulkCreditNoteNegative.value,
      confirm_service_lines: bulkConfirmServiceLines.value,
      accounting_date_months_back: accountingDateMonthsBack.value
    });

    if (result.success) {
      showClientsCompaniesModal.value = false;
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error('Failed to save clients scope:', error);
    alert('Failed to save settings. Please try again.');
  }
};

const openAccountingDateSettingsModal = () => {
  accountingDateMonthsBack.value = authStore.accountingDateMonthsBack ?? 12;
  showAccountingDateSettingsModal.value = true;
};

const closeAccountingDateSettingsModal = () => {
  showAccountingDateSettingsModal.value = false;
};

const saveAccountingDateSettings = async () => {
  try {
    const result = await authStore.updateSettings({
      enforce_subtotal_validation: bulkValidationSetting.value,
      validate_vat_conflicts: bulkVatConflictValidation.value,
      validate_company_on_scan: bulkCompanyScanValidation.value,
      use_warehouse: bulkUseWarehouse.value,
      use_vendor_code: bulkUseVendorCode.value,
      gas_station_multi_invoice: bulkGasStationMultiInvoice.value,
      use_clients: bulkUseClients.value,
      use_accounting_date: bulkUseAccountingDate.value,
      credit_note_negative_amounts: bulkCreditNoteNegative.value,
      confirm_service_lines: bulkConfirmServiceLines.value,
      accounting_date_months_back: accountingDateMonthsBack.value
    });
    
    if (result.success) {
      console.log('Accounting date settings saved successfully');
      showAccountingDateSettingsModal.value = false;
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error('Failed to save accounting date settings:', error);
    alert('Failed to save settings. Please try again.');
  }
};

onMounted(() => {
  loadCompanies();
});

/* How far back the accounting date may be set. */
const monthsBackOptions = computed(() => [
  { value: 0, label: t('companies.outsideCurrentMonth', 'Извън текущия месец') },
  { value: 1, label: t('companies.oneMonth', '1 месец') },
  { value: 3, label: t('companies.threeMonths', '3 месеца') },
  { value: 6, label: t('companies.sixMonths', '6 месеца') },
  { value: 12, label: t('companies.twelveMonths', '12 месеца') },
]);
</script>

<style scoped>
.companies-page {
  min-height: 100vh;
  background: var(--bg-gray);
  padding: 24px 20px;
}

.companies-container {
  max-width: 1366px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--text-secondary);
  pointer-events: none;
}

.search-input {
  padding: 11px 16px 11px 44px;
  border: 2px solid var(--border);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  background: white;
  width: 300px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
}

.search-input::placeholder {
  color: var(--text-light);
}

.page-title {
  font-size: 30px;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
}

.btn-create svg {
  vertical-align: middle;
}

.btn-create:hover {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(14, 165, 233, 0.45);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 13px 28px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
}

.btn-primary svg {
  vertical-align: middle;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(14, 165, 233, 0.45);
}

.companies-grid {
  display: grid;
  /* Four to a row, not as many as fit: at 1366px the auto-fill was choosing
     three and leaving a column of air. The minimum keeps a card from being
     squeezed narrower than a company name needs. */
  grid-template-columns: repeat(4, minmax(240px, 1fr));
  gap: 20px;
}

@media (max-width: 1200px) {
  .companies-grid {
    grid-template-columns: repeat(3, minmax(220px, 1fr));
  }
}

@media (max-width: 900px) {
  .companies-grid {
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  }
}

/* One pixel, in both states.
 *
 * The card was framed at 2px and the hover only changed that frame's colour,
 * so a thick blue line appeared around whatever the pointer touched — the
 * heaviest thing on a page of hairlines. The line stays 1px and the hover
 * says its piece with the colour and a touch of shadow instead. */
.company-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid var(--border);
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.company-card:hover {
  border-color: var(--primary);
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.12);
  background: white;
}

.company-card.inactive {
  opacity: 0.5;
  background: #f9fafb;
}

.company-card.inactive:hover {
  opacity: 0.7;
  background: #f3f4f6;
  border-color: #9ca3af;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.company-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

.company-name-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.company-name {
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.01em;
}

.inactive-label {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #f87171;
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.company-actions {
  display: flex;
  gap: 4px;
}

/* Nothing of its own: the look is set once, below, with the page's other
   quiet actions. This rule used to repaint it white with a grey mark. */
.company-actions .btn-icon {
  flex-shrink: 0;
}

/* The card's two actions, drawn like the dashboard's row actions: just the
 * mark, in its own colour, and no outline in either state. The border stays
 * in the box at zero visibility so the geometry does not change under the
 * pointer. Deleting keeps its own red. */
.btn-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon-danger {
  color: #e04545;
}

/* Hovering lifts the mark two pixels and leaves the background alone — the
   same gesture as the navigation's icons and the dashboard's row actions. */
.btn-icon:hover {
  background: transparent;
  transform: translateY(-2px);
}

.company-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  gap: 8px;
}

.detail-label {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 13px;
  color: var(--text-primary);
  flex: 1;
}

@media (max-width: 768px) {
  .companies-container {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 13px;
  }

  .header-actions {
    flex-direction: column;
    gap: 10px;
  }

  .search-box {
    width: 100%;
  }

  .search-input {
    width: 100%;
  }

  .btn-create {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
  }

  .companies-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .company-card {
    padding: 16px;
  }

  /* The name and its two actions stay on one line.
   *
   * They were stacked here, which cost a whole row of the card to hold two
   * 28px buttons and left them floating under the name with nothing beside
   * them. Two marks fit next to any name — the name takes what is left and
   * ellipsises if it has to. */
  .company-header {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }

  .company-name-wrapper {
    min-width: 0;
    flex: 1;
  }

  .company-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .company-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

/* Settings Button
 *
 * The same quiet action the dashboard uses for swapping company and showing
 * the upload form: a hairline border, no fill, blue text, and a tint with a
 * blue edge on hover. It was a 2px frame with a shadow that lifted four
 * pixels on hover — which is a lot of movement for a link to a panel. */
.btn-settings {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  margin-top: 12px;
  background: transparent;
  color: var(--primary);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-settings:hover {
  background: var(--primary-light);
  border-color: var(--primary);
}

/* Settings Section (inline dashboard-style) */
.settings-section {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.settings-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid var(--border);
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.settings-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--primary);
}

.settings-section-title h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.btn-close-settings {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-settings:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: #fef2f2;
}

.settings-section-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Settings Cards */
.settings-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.settings-card.active {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.settings-card-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.settings-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.settings-icon-amber {
  background: #f59e0b;
}

.settings-icon-blue {
  background: var(--primary);
}

.settings-icon-orange {
  background: #f97316;
}

.settings-icon-green {
  background: #10b981;
}

.settings-info {
  flex: 1;
  min-width: 0;
}

.settings-info h3 {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.settings-info p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

.settings-gear-btn {
  background: white;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-secondary, #6b7280);
  flex-shrink: 0;
  margin-top: 4px;
}

.settings-gear-btn:hover {
  background: var(--primary, #4f8cff);
  color: white;
  border-color: var(--primary, #4f8cff);
}

/* Toggle Switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Drawn like the icon beside it: a rail with a handle on it.
 *
 * It was a solid blue pill with a white knob — the heaviest mark in a row of
 * hairline borders and thin glyphs, and the only thing on the card that
 * shouted. Now it is a 1.5px outline with a small filled handle: grey and to
 * the left when off, blue and to the right when on. The state reads from the
 * handle's side and the colour of the line, which is how the slider icon in
 * the heading reads too. */
.toggle-track {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  border: 1.5px solid #cbd5e1;
  border-radius: 20px;
  transition: all 0.2s ease;
}

.toggle-track::before {
  content: '';
  position: absolute;
  height: 10px;
  width: 10px;
  left: 3px;
  top: 50%;
  margin-top: -5px;
  background: #64748b;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.toggle input:checked + .toggle-track {
  background: transparent;
  border-color: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  background: var(--primary);
  transform: translateX(17px);
}

.toggle:hover .toggle-track {
  border-color: var(--primary);
}

.toggle input:focus-visible + .toggle-track {
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  box-sizing: border-box;
}

.modal-container {
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  margin: auto;
  position: relative;
}

.modal-content {
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.accounting-date-hint {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.clients-scope-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}

.clients-scope-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
}

.clients-scope-option:hover {
  border-color: var(--primary);
  background: #f8fafc;
}

.clients-scope-option input[type="radio"] {
  margin-top: 3px;
  cursor: pointer;
  accent-color: var(--primary);
}

.clients-scope-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.clients-scope-hint {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.clients-companies-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px;
  background: #f9fafb;
}

.clients-company-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);
  user-select: none;
  transition: background 0.15s ease;
}

.clients-company-row:hover {
  background: white;
}

.clients-company-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--primary);
}

.clients-companies-empty {
  padding: 16px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  transition: all 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--primary);
  color: white;
}

.btn-primary:hover {
  background: #0ea5e9;
}

.btn-secondary {
  background: #f3f4f6;
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* `.btn-icon` was declared a second time down here, undoing the border and
   the colour set for it above. The one definition is the one near the cards
   it belongs to. */

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #4b5563;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #4b5563;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* The row of answers under a question — "delete these seven?" — is a footer
   like any other: a hairline above it and the two buttons at its end. */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #dde3ea;
    background: #fff;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* The search field, like every other field in the app: a hairline that turns
   blue. It carried a 2px edge and, on focus, a 4px halo around it — the
   heaviest outline on the page, around the quietest control. */
.search-input {
  height: 32px;
  padding: 0 12px 0 32px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 400;
  box-shadow: none;
  transition: border-color 0.15s ease;
}

.search-input:hover {
  border-color: #4b5563;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: none;
}

.search-icon {
  left: 11px;
  color: #4b5563;
}

.search-icon svg {
  width: 13px;
  height: 13px;
}

/* ── One frame, not two ────────────────────────────────────────────────
 *
 * The chrome above gives a hairline, a 12px corner and a shadow to both
 * .modal-content and .modal-container — but in this dialogue the container
 * is only the box that centres the card, and in one case it is far wider
 * than the card, so its edge was drawn as a rectangle standing in the empty
 * space beside the dialogue. The card keeps the frame; the wrapper carries
 * none. */
.modal-container {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
</style>
